import React, { useEffect, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { isQuickChargeOrderCode } from 'shared-models/browser';
import { ordersService, type OrderView } from '../../../../../services/orders/orders.service';
import { orderlinesService, type OrderlineView } from '../../../../../services/orderlines/orderlines.service';
import { paymentsService, type PaymentlineView } from '../../../../../services/payments/payments.service';
import { toHebrewApiErrorMessage } from '../../../../../services/index/index.service';
import { GlobalDiscountModal } from '../../receipt/actions/global-discount-modal/GlobalDiscountModal';
import { ReceiptActionsModal, type ReceiptActionKey } from '../../receipt/actions/ReceiptActionsModal';
import { ApprovalRequestModal } from '../approval-request-modal/ApprovalRequestModal';
import { useApprovals } from '../section/useApprovals';
import {
  computeOrderMoneyBreakdown,
  resolveGlobalDiscountPersistence,
  resolveGlobalDiscountScopeForPayments,
} from '../shared/discount.utils';
import { OrderItemActionsModals, REMOVE_DISCOUNT_DISABLED_MESSAGE } from './OrderItemActionsModals';
import { useOrderItemActionsFlow } from './useOrderItemActionsFlow';

export type OrderItemActionsFlowProps = {
  tenantId: string;
  employeeSession: string | null;
  activeSessionId: number | null;
  isManager: boolean;
  order: OrderView;
  onClose: () => void;
};

// Summary: Item actions (הנחה/על חשבון הבית/ביטול) for any order, hosted over the current screen.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-orderssection
export const OrderItemActionsFlow: React.FC<OrderItemActionsFlowProps> = ({
  tenantId,
  employeeSession,
  activeSessionId,
  isManager,
  order,
  onClose,
}) => {
  const [isActionsOpen, setIsActionsOpen] = useState(true);
  const [isGlobalDiscountOpen, setIsGlobalDiscountOpen] = useState(false);
  const [isSavingDiscount, setIsSavingDiscount] = useState(false);
  const discountInFlightRef = useRef(false);
  const [orderlines, setOrderlines] = useState<OrderlineView[]>([]);
  const [paymentlines, setPaymentlines] = useState<PaymentlineView[]>([]);
  const [reloadToken, setReloadToken] = useState(0);
  const orderId = order.id;
  const approvals = useApprovals({
    tenantId,
    employeeSession,
    activeSessionId,
    orderId,
    isManager,
    loadPending: false,
    onApprovalSubmitted: onClose,
  });

  useEffect(() => {
    if (!employeeSession) return undefined;
    let isStale = false;
    Promise.all([
      orderlinesService.listForOrder(tenantId, orderId, employeeSession),
      paymentsService.listForOrder(tenantId, orderId, employeeSession),
    ]).then(([lines, payments]) => {
      if (isStale) return;
      setOrderlines(lines);
      setPaymentlines(payments);
    }).catch(() => {
      if (isStale) return;
      notifications.show({ color: 'red', title: 'שגיאה', message: 'שגיאה בטעינת ההזמנה', autoClose: 3000 });
      onClose();
    });
    return () => { isStale = true; };
  }, [tenantId, orderId, employeeSession, reloadToken, onClose]);

  const isTakeAway = order.orderType === 'take_away';
  const isQuickCharge = isQuickChargeOrderCode(order.orderCode);
  const { remainingAfterGlobalDiscount, discount, paidAmount } = computeOrderMoneyBreakdown({ order, orderlines, paymentlines, isTakeAway });

  const flow = useOrderItemActionsFlow({
    tenantId,
    employeeSession,
    orderId,
    isManager,
    isTakeAway,
    orderlines,
    paymentlines,
    remainingAfterGlobalDiscount,
    requestApproval: approvals.requestApproval,
    onDismiss: onClose,
  });

  const handleSelectAction = (key: ReceiptActionKey) => {
    setIsActionsOpen(false);
    // Re-read the rows so the ceiling reflects payments/lines landed since this flow opened.
    setReloadToken((token) => token + 1);
    if (key === 'globalDiscount') {
      setIsGlobalDiscountOpen(true);
      return;
    }
    flow.openStep(key);
  };

  const handleRemoveGlobalDiscount = async () => {
    if (!employeeSession || !isManager || discountInFlightRef.current) return;
    discountInFlightRef.current = true;
    setIsSavingDiscount(true);
    try {
      await ordersService.updateGlobalDiscount(tenantId, orderId, null, null, employeeSession);
      setIsGlobalDiscountOpen(false);
      onClose();
    } catch (err) {
      notifications.show({ title: 'שגיאה בהסרת הנחה', message: toHebrewApiErrorMessage(err instanceof Error ? err.message : ''), color: 'red', autoClose: 3000 });
    } finally {
      setIsSavingDiscount(false);
      discountInFlightRef.current = false;
    }
  };

  return (
    <>
      <ReceiptActionsModal
        opened={isActionsOpen}
        onClose={onClose}
        onSelectAction={handleSelectAction}
        isTakeAway={isTakeAway}
        isQuickCharge={isQuickCharge}
      />

      <GlobalDiscountModal
        isOpen={isGlobalDiscountOpen}
        remainingToPay={remainingAfterGlobalDiscount}
        existingDiscountAmount={discount}
        isSaving={isSavingDiscount}
        onClose={onClose}
        removeDisabledMessage={isManager ? undefined : REMOVE_DISCOUNT_DISABLED_MESSAGE}
        onRemoveDiscount={() => { void handleRemoveGlobalDiscount(); }}
        onConfirm={(discountAmount, mode, scope, discountInput) => {
          if (!employeeSession || discountInFlightRef.current) return;
          setIsGlobalDiscountOpen(false);
          const persisted = resolveGlobalDiscountPersistence(mode, resolveGlobalDiscountScopeForPayments(scope, paidAmount), discountAmount, discountInput);
          approvals.requestApproval('global_discount', {
            actionType: 'global_discount',
            discountMode: persisted.discountMode,
            discountInput: persisted.discountInput,
            discountAmount,
          });
        }}
      />

      <OrderItemActionsModals flow={flow} />

      {approvals.approvalRequestModalState && (
        <ApprovalRequestModal
          opened={approvals.approvalRequestModalState.opened}
          onClose={onClose}
          actionType={approvals.approvalRequestModalState.actionType}
          payload={approvals.approvalRequestModalState.payload}
          orderlines={orderlines}
          isSubmitting={approvals.isSubmittingApproval}
          isManager={isManager}
          onSubmit={approvals.submitApproval}
        />
      )}
    </>
  );
};
